/**
 * RoyaleResultModal — the end-of-game popup shown to BOTH players and
 * spectators the moment a Puzzle Royale ends. Replaces the old inline
 * RoyaleEndScreen block with a centred modal that:
 *   - keeps every stat the inline end screen showed (your rank / points /
 *     solved / best combo) for players,
 *   - ADDS the final standings (leaderboard) so spectators — who previously
 *     saw only a title — get the result too,
 *   - keeps the host's Rematch action.
 * Closing is the parent's call (player → Fun Challenges; spectator → stop
 * watching).
 */
import React from 'react';
import {Pressable, ScrollView, StyleSheet, View} from 'react-native';

import CCText from '@/Atoms/CCText';
import SlideModal from '@/Atoms/SlideModal';
import {FontSizes} from '@/Utils/Dimensions';

import type {PlayerRoyaleState} from '@/Screens/FunChallenges/royaleTypes';

const TEAL = '#3DAB9E';
const BROWN = '#4D3F37';
const TEXT_DARK = '#262322';
const TEXT_MUTED = '#8A7E5F';
const CREAM3 = '#EDE4CC';
const GOLD = '#E7B325';

interface Props {
  visible: boolean;
  /** Undefined for spectators (they aren't ranked). */
  me?: PlayerRoyaleState | null;
  /** 0-based rank of `me`. */
  myRank?: number;
  isSpectator?: boolean;
  players: Record<string, PlayerRoyaleState>;
  /** Final order, winner first. */
  leaderboard: string[];
  canRematch?: boolean;
  onRematch?: () => void;
  onClose: () => void;
}

export default function RoyaleResultModal({
  visible,
  me,
  myRank,
  isSpectator,
  players,
  leaderboard,
  canRematch,
  onRematch,
  onClose,
}: Props) {
  const totalPlayers = leaderboard.length;
  const winnerName = leaderboard.length
    ? players[leaderboard[0]]?.name || 'Winner'
    : '';

  return (
    <SlideModal
      visible={visible}
      onClose={() => onClose()}
      modalHeaderTitle="Puzzle Royale ended"
      type="center"
      contentPadding={20}>
      <View style={styles.body}>
        {/* Winner callout */}
        {!!winnerName && (
          <View style={styles.winnerRow}>
            <CCText style={styles.winnerCrown}>👑</CCText>
            <CCText style={styles.winnerText} numberOfLines={1}>
              {winnerName} wins
            </CCText>
          </View>
        )}

        {/* Your stats — players only (spectators skip straight to standings). */}
        {me && !isSpectator && (
          <View style={styles.summary}>
            <Stat
              label="Your rank"
              value={myRank != null ? `#${myRank + 1} / ${totalPlayers}` : '—'}
            />
            <Divider />
            <Stat label="Points" value={String(me.points)} />
            <Divider />
            <Stat label="Solved" value={String(me.solved)} />
            <Divider />
            <Stat label="Best combo" value={String(me.bestCombo)} />
          </View>
        )}

        {/* Final standings — the full ranked list, for players AND spectators. */}
        <View style={styles.standings}>
          <CCText style={styles.standingsTitle}>Final standings</CCText>
          <ScrollView style={styles.standingsList} nestedScrollEnabled>
            {leaderboard.map((id, idx) => {
              const p = players[id];
              if (!p) return null;
              const isYou = !isSpectator && !!me && id === me.userId;
              return (
                <View
                  key={id}
                  style={[styles.standRow, isYou && styles.standRowYou]}>
                  <View
                    style={[styles.rankBadge, idx === 0 && styles.rankBadgeGold]}>
                    <CCText
                      style={[
                        styles.rankBadgeText,
                        idx === 0 && styles.rankBadgeTextGold,
                      ]}>
                      {idx + 1}
                    </CCText>
                  </View>
                  <CCText style={styles.standName} numberOfLines={1}>
                    {p.name || 'Player'}
                    {isYou ? ' (You)' : ''}
                    {p.eliminated ? ' · out' : ''}
                  </CCText>
                  <CCText style={styles.standPts}>{p.points} pts</CCText>
                </View>
              );
            })}
          </ScrollView>
        </View>

        {/* Actions */}
        <View style={styles.actions}>
          {canRematch && (
            <Pressable onPress={onRematch} style={styles.rematchBtn}>
              <CCText style={styles.rematchText}>Rematch</CCText>
            </Pressable>
          )}
          <Pressable onPress={() => onClose()} style={styles.closeBtn}>
            <CCText style={styles.closeText}>
              {isSpectator ? 'Stop watching' : 'Close'}
            </CCText>
          </Pressable>
        </View>
        {canRematch && (
          <CCText style={styles.rematchHint}>
            Rematch starts a fresh race with the same settings — everyone here
            joins automatically.
          </CCText>
        )}
      </View>
    </SlideModal>
  );
}

function Stat({label, value}: {label: string; value: string}) {
  return (
    <View style={styles.stat}>
      <CCText style={styles.statLabel}>{label}</CCText>
      <CCText style={styles.statValue}>{value}</CCText>
    </View>
  );
}

function Divider() {
  return <View style={styles.divider} />;
}

const styles = StyleSheet.create({
  body: {gap: 18, minWidth: 320},
  winnerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  winnerCrown: {fontSize: FontSizes[20] || 20},
  winnerText: {
    fontSize: FontSizes[18] || 18,
    fontWeight: '800',
    color: BROWN,
    flexShrink: 1,
  },
  summary: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  stat: {alignItems: 'center', flex: 1},
  statLabel: {
    fontSize: FontSizes[10] || 10,
    fontWeight: '700',
    color: TEXT_MUTED,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 4,
  },
  statValue: {fontSize: FontSizes[18] || 18, fontWeight: '800', color: BROWN},
  divider: {width: 1, height: 32, backgroundColor: CREAM3},

  standings: {gap: 8},
  standingsTitle: {
    fontSize: FontSizes[12] || 12,
    fontWeight: '800',
    color: TEXT_MUTED,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  standingsList: {maxHeight: 240},
  standRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 8,
    paddingHorizontal: 8,
    borderRadius: 8,
  },
  standRowYou: {backgroundColor: 'rgba(61,171,158,0.12)'},
  rankBadge: {
    width: 26,
    height: 26,
    borderRadius: 999,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: CREAM3,
  },
  rankBadgeGold: {backgroundColor: GOLD},
  rankBadgeText: {fontSize: FontSizes[13] || 13, fontWeight: '800', color: BROWN},
  rankBadgeTextGold: {color: '#fff'},
  standName: {flex: 1, fontSize: FontSizes[15] || 15, fontWeight: '700', color: TEXT_DARK},
  standPts: {fontSize: FontSizes[14] || 14, fontWeight: '800', color: BROWN},

  actions: {flexDirection: 'row', alignItems: 'center', gap: 12},
  rematchBtn: {
    flex: 1,
    backgroundColor: TEAL,
    borderRadius: 999,
    paddingVertical: 11,
    alignItems: 'center',
  },
  rematchText: {fontSize: FontSizes[14] || 14, fontWeight: '800', color: '#fff'},
  closeBtn: {
    flex: 1,
    backgroundColor: '#F0EADB',
    borderRadius: 999,
    paddingVertical: 11,
    alignItems: 'center',
  },
  closeText: {fontSize: FontSizes[14] || 14, fontWeight: '800', color: BROWN},
  rematchHint: {fontSize: FontSizes[12] || 12, color: TEXT_MUTED, textAlign: 'center'},
});
